import { useEffect, useState } from 'react';
import { getNotificaciones } from '../../../services/Busquedas/Afiliados';
import { useInfoAfiliado } from '../../../components/Afiliados/InfoAfiliadoProvider';
import { deshabilitarNotificacion } from '../../../services/Afiliado/Afiliado';
import { useNotificacionesContext } from './useNotificacionesContext';

/**
 * useNotificaciones - Hook para gestionar notificaciones
 *
 * Maneja:
 * - Carga de notificaciones desde el servidor
 * - Actualización del contexto global de notificaciones
 * - Marca notificaciones como leídas
 * - Sincronización del contador global de notificaciones
 * - Control de estado de carga (loading)
 *
 * @returns {object} Estado y acciones de notificaciones
 */
export const useNotificaciones = () => {
  const { infoAfiliado } = useInfoAfiliado();
  const { setNotificaciones, removerNotificacion, contadorNotificaciones } =
    useNotificacionesContext();
  const [notificacionesList, setNotificacionesList] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Carga las notificaciones desde el servidor cuando hay cambios en infoAfiliado
   */
  useEffect(() => {
    if (infoAfiliado) {
      const getNotificacionesRequest = async () => {
        try {
          setLoading(true);
          const result = await getNotificaciones(infoAfiliado?.afiliado?.idAfiliado);
          setNotificacionesList(result);
          // Actualiza el contexto global con las nuevas notificaciones
          setNotificaciones(result);
        } catch (error) {
          console.error('Error al cargar notificaciones:', error);
        } finally {
          setLoading(false);
        }
      };

      getNotificacionesRequest();
    }
  }, [infoAfiliado, setNotificaciones]);

  /**
   * Marca una notificación como leída
   *
   * - Elimina la notificación del servidor
   * - Actualiza el contexto global
   * - Decrementa el contador
   *
   * @param {object} notificacion - Notificación a marcar como leída
   * @returns {boolean} true si fue exitoso, false si hubo error
   */
  const leerNotificacion = async (notificacion) => {
    try {
      setLoading(true);
      const resultado = await deshabilitarNotificacion(notificacion);

      if (resultado) {
        // Remueve del estado local
        setNotificacionesList((prev) =>
          prev.filter((n) => n.numeroNotificacion !== notificacion.numeroNotificacion),
        );

        // Remueve del contexto global y decrementa contador
        removerNotificacion(notificacion.numeroNotificacion);

        return true;
      }
      return false;
    } catch (error) {
      console.error('Error al deshabilitar la notificación:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    leerNotificacion,
    notificacionesList,
    notificacionesCount: contadorNotificaciones,
    loading,
  };
};
